'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, BarChart2, MapPin, Zap, Brain, FileText, GitCompare, Phone } from 'lucide-react';

const services = [
  {
    id: 'svc-1',
    icon: Phone,
    title: 'Expert Admission Counselling',
    description: 'One-on-one sessions with certified NEET counsellors. AIQ, State quota, Deemed, and NRI quota specialists guide you from rank to admission.',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
    href: '/student-dashboard',
    badge: 'Most Popular',
  },
  {
    id: 'svc-2',
    icon: FileText,
    title: 'Choice Filling Strategy',
    description: 'Get an AI-optimised college preference order with round-wise strategy, probability scores, and expert counsellor review before submission.',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
    href: '/student-dashboard',
    badge: null,
  },
  {
    id: 'svc-3',
    icon: MapPin,
    title: 'College Database',
    description: '2,847+ verified medical colleges with fees, seats, NAAC grade, cutoffs, infrastructure details and more.',
    color: 'text-success',
    bg: 'bg-success/8',
    href: '/medical-colleges-database',
    badge: null,
  },
  {
    id: 'svc-4',
    icon: GitCompare,
    title: 'College Comparator',
    description: 'Compare up to 4 colleges side-by-side on fees, seats, bond, hostel, ranking, cutoff, and infrastructure.',
    color: 'text-warning',
    bg: 'bg-warning/8',
    href: '/compare-colleges',
    badge: null,
  },
  {
    id: 'svc-5',
    icon: Brain,
    title: 'AI NEET College Predictor',
    description: 'New feature — AI-powered prediction engine with 97% accuracy. Get your personalised college list based on rank, category, state, and quota.',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
    href: '/neet-college-predictor',
    badge: 'New Feature',
  },
  {
    id: 'svc-6',
    icon: BarChart2,
    title: 'Cutoff Intelligence',
    description: 'Interactive cutoff trend charts for 2022–2026. State-wise demand analysis and seat distribution visualizations.',
    color: 'text-[#1a3a8f]',
    bg: 'bg-[#1a3a8f]/8',
    href: '/neet-college-predictor',
    badge: null,
  },
];

export default function ServicesSection() {
  return (
    <section className="py-24 px-6 lg:px-8 relative overflow-hidden">
      <div className="floating-orb w-96 h-96 bg-[#1a3a8f]/4 top-10 right-0 animate-float-delay" />
      <div className="max-w-screen-xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full text-xs text-[#1a3a8f] border border-[#1a3a8f]/15 mb-4 font-semibold">
            <Zap size={12} />
            <span>Complete Admission Counselling & Intelligence Suite</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4 tracking-tight">
            Everything You Need to <span className="gradient-text">Secure Your Admission</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From expert counselling to AI prediction — one platform for your entire NEET admission journey.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services?.map((svc, i) => (
            <Link
              key={svc?.id}
              href={svc?.href}
              className={`glass-card-hover rounded-2xl p-6 border border-white/50 group relative overflow-hidden ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              {svc?.badge && (
                <span className="absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full bg-[#1a3a8f]/10 text-[#1a3a8f] border border-[#1a3a8f]/20">
                  {svc?.badge}
                </span>
              )}
              <div className={`w-12 h-12 rounded-2xl ${svc?.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <svc.icon size={22} className={svc?.color} />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">{svc?.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{svc?.description}</p>
              <div className={`flex items-center gap-1.5 text-sm font-semibold ${svc?.color} group-hover:gap-2.5 transition-all duration-200`}>
                <span>Explore</span>
                <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>

        {/* How It Works */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold text-[#1a3a8f] uppercase tracking-widest mb-2">Simple Process</p>
            <h3 className="text-2xl font-bold text-foreground">Get Expert Counselling in <span className="gradient-text">3 Easy Steps</span></h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 relative">
            <div className="hidden sm:block absolute top-8 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-[#1a3a8f]/30 via-[#1a3a8f] to-[#1a3a8f]/30 z-0" />
            {[
              { step: '01', title: 'Book a Free Session', desc: 'Schedule a free 30-minute consultation with a certified NEET admission counsellor at your convenience.', color: 'bg-[#1a3a8f]/10 text-[#1a3a8f] border-[#1a3a8f]/20' },
              { step: '02', title: 'Get Your Strategy', desc: 'Your counsellor analyses your rank, category, and preferences to build a personalised admission roadmap.', color: 'bg-[#1a3a8f]/10 text-[#1a3a8f] border-[#1a3a8f]/20' },
              { step: '03', title: 'Secure Your Seat', desc: 'Follow expert guidance through choice filling, document verification, and every counselling round until admission.', color: 'bg-success/10 text-success border-success/20' },
            ]?.map((item) => (
              <div key={item?.step} className="relative z-10 glass-card-white rounded-2xl p-6 border border-slate-100 text-center shadow-sm">
                <div className={`w-14 h-14 rounded-2xl border-2 ${item?.color} flex items-center justify-center mx-auto mb-4 text-xl font-extrabold`}>
                  {item?.step}
                </div>
                <h4 className="font-bold text-foreground text-sm mb-2">{item?.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}